import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAnalytics, usePageView } from '@govard/analytics';
import { useTranslation } from '@govard/i18n-core/react';
import { useFeatureFlag } from '@govard/feature-flags/react';

import { useMetrikaRouteHits } from '../../analytics/metrika.js';
import { AuthGuard } from '../../shared/auth/AuthGuard.js';
import { CRM_FLAG_IDS } from '../../feature-flags/index.js';
import { QuickNoteButton } from '../../features/notes/components/QuickNoteButton.js';
import { GlobalCommandMenu } from '../../features/command-menu/GlobalCommandMenu.js';
import { KeyboardShortcutsModal } from '../../features/shortcuts/components/KeyboardShortcutsModal.js';
import { PendingInvitationsBanner } from '../../features/team/components/PendingInvitationsBanner.js';
// Быстрое создание из командного меню: переиспользуются существующие формы
// фич, отдельного «командного» редактора не заводим.
import { DealFormModal } from '../../features/deals/components/DealFormModal.js';
import { ContactFormModal } from '../../features/contacts/components/ContactFormModal.js';
import { TaskFormModal } from '../../features/tasks/components/TaskFormModal.js';
// PR #974 (AI cockpit): exactly one shell-level chat client action executor and
// the docked cockpit panel live here, so the whole app shares one router owner.
import { useChatClientActionExecutor } from '../../features/ai-chat/hooks/useChatClientActionExecutor.js';
import { ChatCockpitPanel } from '../../features/ai-chat/components/ChatCockpitPanel.js';
import {
  isChatCockpitRoute,
  resolveChatCockpitContext,
} from '../../features/ai-chat/lib/chatCockpitContext.js';
import { useSelectionStore } from '../../shared/selection/useSelectionStore.js';
import { RightSidebar } from './RightSidebar.js';
import { SystemAgentPanel } from './SystemAgentPanel.js';
import { Sidebar } from './Sidebar.js';
import { Topbar } from './Topbar.js';

/** Что открыто через «Создать …» из командного меню. */
type QuickCreateKind = 'deal' | 'contact' | 'task';

function AppShell(): ReactElement {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const aiChat = useFeatureFlag(CRM_FLAG_IDS.AI_CHAT);
  const aiCockpit = useFeatureFlag(CRM_FLAG_IDS.AI_COCKPIT);

  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [cockpitOpen, setCockpitOpen] = useState(true);
  const [systemAgentOpen, setSystemAgentOpen] = useState(false);
  const [commandMenuOpen, setCommandMenuOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickCreate, setQuickCreate] = useState<QuickCreateKind | null>(null);

  // The docked cockpit needs the record the person is looking at. The selection
  // registry is read here and turned into a validated context by a pure mapper,
  // so the panel never derives identity from a label.
  const selectionKind = useSelectionStore((state) => state.kind);
  const selectionItems = useSelectionStore((state) => state.items);
  const cockpitContext = useMemo(
    () =>
      resolveChatCockpitContext(location.pathname, { kind: selectionKind, items: selectionItems }),
    [location.pathname, selectionKind, selectionItems],
  );

  const cockpitRoute = isChatCockpitRoute(location.pathname);
  // On a supported route the topbar context toggle now drives the cockpit
  // instead of the right sidebar; everywhere else it keeps its legacy meaning.
  const cockpitToggleActive = aiCockpit && cockpitRoute;
  const shouldShowCockpit = cockpitToggleActive && cockpitOpen;
  // The legacy hidden slot only survives while the cockpit is not taking over.
  const shouldShowAiSlot = aiChat && isAiContextRoute(location.pathname) && !shouldShowCockpit;
  const shouldShowContextSidebar = rightSidebarOpen && !shouldShowAiSlot && !shouldShowCockpit;

  // Single app-wide executor for native-chat client actions (see the hook header):
  // it is mounted exactly once, here, and `/chat` no longer touches the bus.
  useChatClientActionExecutor();

  // Fire once on the very first paint (usePageView handles `window` guards).
  usePageView();

  // Yandex.Metrika hit on every SPA route change (the snippet in index.html
  // already reported the initial pageview).
  useMetrikaRouteHits();

  // Fire on subsequent pathname changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    analytics.track('screen_view', {
      screen: location.pathname,
      product: 'crm',
      lang: 'ru',
    });
  }, [analytics, location.pathname]);

  // The mobile nav drawer must not survive a breakpoint crossing: from md
  // (768px) up the persistent aside rail is the only sidebar surface. Without
  // this reset, rotating a phone or resizing a window keeps the drawer stacked
  // on top of the rail — two nav overlays with the same content at once.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(min-width: 768px)');
    const handleChange = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        setMobileMenuOpen(false);
      }
    };
    if (typeof media.addEventListener !== 'function') return;
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  // Global keyboard shortcuts (Cmd+K / Ctrl+K, Cmd+J / Ctrl+J, '?' for cheatsheet, 'G'+'D/C/T/A' navigation chords)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let lastKey = '';
    let lastKeyTime = 0;

    const handleKeyDown = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement | null;
      const isInput =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      if (isCmdOrCtrl && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCommandMenuOpen((prev) => !prev);
        return;
      }

      if (isCmdOrCtrl && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setSystemAgentOpen((prev) => !prev);
        return;
      }

      if (isInput) return;

      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShortcutsModalOpen((prev) => !prev);
        return;
      }

      const now = Date.now();
      if (lastKey === 'g' && now - lastKeyTime < 1000) {
        const key = e.key.toLowerCase();
        if (key === 'd') {
          e.preventDefault();
          navigate('/deals');
        } else if (key === 'c') {
          e.preventDefault();
          navigate('/contacts');
        } else if (key === 't') {
          e.preventDefault();
          navigate('/tasks');
        } else if (key === 'a') {
          e.preventDefault();
          navigate('/dashboard');
        }
        lastKey = '';
        return;
      }

      if (e.key.toLowerCase() === 'g') {
        lastKey = 'g';
        lastKeyTime = now;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return (
    <div
      className="flex h-screen w-screen overflow-hidden transition-colors duration-200"
      style={{
        background: 'var(--ant-color-bg-layout, #f9f7f4)',
        color: 'var(--ant-color-text, #292524)',
      }}
    >
      <Sidebar mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          rightSidebarOpen={shouldShowContextSidebar || shouldShowCockpit}
          onToggleRightSidebar={() => {
            if (cockpitToggleActive) {
              setCockpitOpen((value) => !value);
              return;
            }
            setRightSidebarOpen((value) => !value);
          }}
          // On phones the cockpit is a right-side drawer and the topbar action
          // group is hidden, so the toggle has to stay reachable there or a
          // closed cockpit could never be reopened.
          mobileContextToggle={cockpitToggleActive}
          systemAgentOpen={systemAgentOpen}
          onToggleSystemAgent={() => {
            setSystemAgentOpen((value) => !value);
          }}
          onOpenCommandMenu={() => {
            setCommandMenuOpen(true);
          }}
          onToggleMobileMenu={() => {
            setMobileMenuOpen((prev) => !prev);
          }}
          mobileMenuOpen={mobileMenuOpen}
        />
        <div className="flex min-h-0 flex-1">
          <main
            className={`min-w-0 flex-1 flex flex-col overflow-hidden ${
              isEdgeToEdgeRoute(location.pathname)
                ? 'bg-[var(--ant-color-bg-container,#fffdfa)]'
                : 'bg-[var(--ant-color-bg-layout)]'
            }`}
            role="main"
            aria-label={t('crm.sidebar.brand')}
          >
            <div
              className={
                isEdgeToEdgeRoute(location.pathname)
                  ? 'h-full w-full overflow-hidden flex flex-col'
                  : 'h-full w-full overflow-y-auto p-6 lg:px-6 lg:py-6'
              }
            >
              <div
                className={
                  isEdgeToEdgeRoute(location.pathname)
                    ? 'flex-1 flex flex-col min-h-0 h-full w-full'
                    : 'max-w-[1600px] mx-auto'
                }
              >
                {/* Пустой баннер не должен давать отступ: на edge-to-edge
                    страницах (карточки контакта/сделки) он оставлял белую полосу. */}
                <div
                  className={isEdgeToEdgeRoute(location.pathname) ? 'mb-4 empty:hidden' : 'mb-4'}
                >
                  <PendingInvitationsBanner />
                </div>
                <Outlet />
              </div>
            </div>
          </main>
          {shouldShowCockpit ? (
            <ChatCockpitPanel
              context={cockpitContext}
              onClose={() => {
                setCockpitOpen(false);
              }}
            />
          ) : null}
          <RightSidebar
            isOpen={shouldShowContextSidebar}
            onClose={() => {
              setRightSidebarOpen(false);
            }}
          />
        </div>
      </div>
      {/* Быстрая заметка доступна на любой странице приложения — кроме чата
          на узких экранах: там плавающая кнопка перекрывала композер и кнопки
          подтверждения карточки. */}
      {location.pathname.startsWith('/chat') ? (
        <div className="hidden md:contents">
          <QuickNoteButton />
        </div>
      ) : (
        <QuickNoteButton />
      )}
      {shouldShowAiSlot ? (
        <aside
          aria-label="AI chat"
          data-testid="crm-chat-panel-slot"
          className="hidden"
          style={{ display: 'none' }}
        >
          {/* TODO [WEEK-4]: mount @govard/ai-chat-kit panel with real scope +
              provider. Hidden when empty so it doesn't reserve 320px width. */}
        </aside>
      ) : null}
      <SystemAgentPanel
        isOpen={systemAgentOpen}
        onClose={() => {
          setSystemAgentOpen(false);
        }}
      />
      <GlobalCommandMenu
        isOpen={commandMenuOpen}
        onClose={() => {
          setCommandMenuOpen(false);
        }}
        onCreateDeal={() => {
          setQuickCreate('deal');
        }}
        onCreateContact={() => {
          setQuickCreate('contact');
        }}
        onCreateTask={() => {
          setQuickCreate('task');
        }}
      />
      {quickCreate === 'deal' ? (
        <DealFormModal
          open
          onClose={() => {
            setQuickCreate(null);
          }}
        />
      ) : null}
      {quickCreate === 'contact' ? (
        <ContactFormModal
          open
          onClose={() => {
            setQuickCreate(null);
          }}
        />
      ) : null}
      {quickCreate === 'task' ? (
        <TaskFormModal
          open
          onClose={() => {
            setQuickCreate(null);
          }}
        />
      ) : null}
      <KeyboardShortcutsModal
        isOpen={shortcutsModalOpen}
        onClose={() => {
          setShortcutsModalOpen(false);
        }}
      />
    </div>
  );
}

function isAiContextRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/deals/') || pathname.startsWith('/contacts/') || pathname === '/hot-leads'
  );
}

function isEdgeToEdgeRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/deals/') ||
    pathname.startsWith('/contacts/') ||
    pathname.startsWith('/companies/') ||
    pathname.startsWith('/tasks/') ||
    pathname === '/chat'
  );
}

export function AppLayout(): ReactElement {
  return (
    <AuthGuard>
      <AppShell />
    </AuthGuard>
  );
}
